import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Modal,
  Button,
  Form,
  RadioGroup,
  Radio,
  CheckboxGroup,
  Checkbox,
  Input,
} from 'tea-component';
import '@/pages/opinion/assets/scss/pages/performance/components/savePopup.scss';
import type {
  FilterKeys,
  KeywordSearchModelType,
  ServerTimeType,
} from '@/pages/opinion/components/FilterCommon/types';
import { logicTextMap } from '@/pages/opinion/components/FilterCommon/constants/regionAndLanguage';
import useTranslate from '@/pages/opinion/components/FilterCommon/hooks/useTranslate';
import { has } from 'lodash-es';
import { dateMap, dealwithDate } from '../utils/time';
import moment from 'moment';
import BubbleCommon, { commonTimeTipsText } from '../../BubbleCommon';
import { dealwithHistoryToRender } from '../utils/historyUtils';
import { deviationKeys } from '../constants/filterOptions';
import {
  gameAffinityPlaceholder,
  genreGamePlaceholder,
  getPlaceholderText,
} from '../constants/placeholder';
import { saveFilterName, setSessionStorage } from '../utils/storage';
import { dealwithChannelToSave } from '../utils/channel';
import { NO_DATE } from '@op/constants';
import { keywordModalTextMap } from '../constants/keyword';
import { LocaleType } from '@op/types/common';
import useSaveFilter from '@op/hooks/useSaveFilter';
import { hasLogicGame } from '../constants/game';
export default function SavePopup({
  saveOpen,
  close: propsClose,
  showData,
  lang,
  gameInfo,
  serverData,
  confirm,
  filter,
  onlySaveFilters = [],
  renderExcludeFilter,
  isAccountRegion,
}: any) {
  const timeShowInfo = useMemo(() => {
    const { date } = showData || {};
    const { date_type, start_time, end_time } = date || {};
    const isHourly = date_type === 'hourly';

    if (isHourly) {
      return [start_time, end_time];
    }

    const { formate } = dateMap[date_type as ServerTimeType] || {};
    return [
      moment(start_time).format(formate),
      moment(end_time).format(formate),
    ];
  }, [showData]);
  const [selectedFilters, setSelectedFilters] = useState<Array<string>>([]);
  const allSelectedFilters = useRef<Array<string>>([]);
  const { formatMessage, translateInfo, needTranslateElement } = useTranslate();

  const [quickShowTime, value] = useMemo(() => {
    const { date_type, start_time, end_time } = serverData || {};
    // if (quickOptions === 'Year to Date') {
    //   return [quickOptions, quickOptions];
    // } else

    if (date_type !== 'hourly') {
      const { before_text, suffix, current } =
        dateMap[date_type as ServerTimeType] || {};
      const value = dealwithDate(start_time, end_time, date_type);
      let showText = current;
      if (value > 0) {
        showText = `${formatMessage({ id: 'Last' })} ${value} ${formatMessage({
          id: suffix,
        })}`;
      } else if (value < 0) {
        showText = formatMessage({ id: before_text });
      }
      return [showText, value];
    }
    return ['Today', 0];
  }, [showData, formatMessage]);

  const [timeType, setTimeType] = useState('quick');

  const {
    verifyName,
    filterName,
    setFilterName,
    tipsMessage,
    saveFilter,
    setTipsMessage,
    loading: saveLoading,
  } = useSaveFilter({ gameInfo });

  const getOptionsElement = (
    text: FilterKeys | 'accountRegion',
    value: any,
    expandText:
      | { bracketsText?: string; outBracketsText?: string }
      | string = '',
  ) => {
    let bracketsText: any = expandText,
      outBracketsText: any = '';
    if (typeof expandText === 'object') {
      bracketsText = expandText.bracketsText;
      outBracketsText = expandText.outBracketsText;
    }

    return (
      <>
        <span className="model-form-text">
          {getPlaceholderText(text)}
          {bracketsText ? `(${bracketsText})` : ''}
          {outBracketsText ? `${outBracketsText}` : ''}
        </span>
        <span className="model-form-value" title={value}>
          {value}
        </span>
      </>
    );
  };

  const filterOptionsElementMap: {
    [key in FilterKeys]?: { key: string; render: any; className?: string };
  } = useMemo(() => {
    return {
      channel: {
        key: 'channel_securitys%_%channel%_%channel_social%_%channel_news%_%channel_comments%_%channel_type',
        render: ({ channel }: any) => {
          return (
            <>
              <span className="model-form-text">
                {formatMessage({ id: 'Channel' })}
              </span>
              <span>{translateInfo(channel)}</span>
            </>
          );
        },
      },
      invalid: {
        key: 'is_valid',
        render: ({ invalid }: any) =>
          getOptionsElement('invalid', translateInfo(invalid)),
      },
      monitor: {
        key: 'social_media_by_account%_%social_media_by_keyword%_%external_data_sources%_%except_social_media_by_keyword%_%except_social_media_by_account%_%except_external_data_sources%_%monitor_source_match_mode%_%account_categories',
        render: ({ monitor, monitor_source_match_mode }: any) =>
          getOptionsElement('monitor', translateInfo(monitor), {
            bracketsText: formatMessage({
              id: monitor_source_match_mode || NO_DATE.EMPTY,
            }),
          }),
      },
      rating: {
        key: 'reviews_rating',
        render: ({ rating }: any) => getOptionsElement('rating', rating),
      },
      matchedType: {
        key: 'matched_type',
        render: ({ matchedType }: any) =>
          getOptionsElement('matchedType', matchedType),
      },
      regionAndLanguage: {
        render: ({ language, logic, region }: any) => {
          return (
            <>
              <span className="model-form-text">
                {formatMessage({ id: 'Language' })}
              </span>
              <span>
                {language}（{logicTextMap[logic]?.[lang]}）
              </span>
              <span className="model-form-text">
                {formatMessage({ id: 'Region' })}
              </span>
              {/* <SvgIcon type="other-county" className="model-form-county" /> */}
              <span>{needTranslateElement(region)}</span>
            </>
          );
        },
        key: 'language%_%logic%_%region%_%region_type',
        className: 'all-line',
      },
      genreGameAndAffinity: {
        key: 'game_covereds%_%affinity_type%_%game_affinitys',
        className: 'all-line',
        render: ({ genreGameList, affinity_type, gameAffinitys }: any) => {
          return (
            <div>
              <span className="model-form-text">
                {genreGamePlaceholder[lang as LocaleType]}
              </span>
              <span>
                {genreGameList}（{logicTextMap[affinity_type]?.[lang]}）
              </span>
              <span className="model-form-text">
                {gameAffinityPlaceholder[lang as LocaleType]}
              </span>
              <span>{gameAffinitys}</span>
            </div>
          );
        },
      },
      language: {
        key: 'language',
        render: ({ language }: any) => getOptionsElement('language', language),
      },
      region: {
        key: 'region%_%logic',
        render: ({ region, logic }: any) =>
          getOptionsElement(
            isAccountRegion ? 'accountRegion' : 'region',
            needTranslateElement(region),
            hasLogicGame(gameInfo) ? formatMessage({ id: logic }) : '',
          ),
      },
      sentiment: {
        key: 'sentiment',
        render: ({ sentiment }: any) =>
          getOptionsElement('sentiment', sentiment),
      },
      topic: {
        key: 'topic%_%topic_rule',
        render: (data: any) => {
          const { topic_rule, topic_type, topic } = data;
          return getOptionsElement('topic', translateInfo(topic), {
            bracketsText: formatMessage({ id: topic_rule || NO_DATE.EMPTY }),
          });
        },
      },
      version: {
        key: 'version_times',
        render: ({ version }: any) =>
          getOptionsElement('version', translateInfo(version)),
      },
      monitorSource: {
        key: 'monitor_scource',
        render: ({ monitorSource }: any) => {
          return getOptionsElement('monitorSource', monitorSource);
        },
      },
      hero: {
        key: 'keyword_en',
        render: ({ hero }: any) => {
          return getOptionsElement('hero', hero);
        },
      },
      hashtag: {
        key: 'keyword',
        render: ({ hashtag }: any) => {
          return getOptionsElement('hashtag', hashtag);
        },
      },
      keyWords: {
        key: 'keyWords%_%keywords_list%_%keywordActiveId%_%keywordCustomInput%_%like_math_keywords%_%like_math_exclude_keywords%_%merge_keywords_list%_%keywords_list_mode',
        render: (data: any) => {
          const {
            keywordSearchType,
            keywordCustomInput,
            keyWords,
            keywordActiveId = 'system',
            keywordListMode = 'include',
          } = data || {}; 
          const modeText = formatMessage({
            id: keywordListMode === 'exclude' ? 'Exclude' : 'Include',
          });

          if (keywordActiveId === 'custom') {
            const type: KeywordSearchModelType = keywordSearchType;
            return (
              <>
                <span className="model-form-text">
                  {formatMessage({
                    id: keywordModalTextMap[type],
                  })}
                  ({modeText})
                </span>
                <span className="model-form-value">
                  {keywordCustomInput
                    ? keywordCustomInput
                    : formatMessage({ id: 'None' })}
                </span>
              </>
            );
          }

          return getOptionsElement('keyWords', keyWords, modeText);
        },
      },
      keywordFilterType: {
        key: 'keyword_filter_types',
        render: ({ keywordFilterType }: any) => {
          return getOptionsElement('keywordFilterType', keywordFilterType);
        },
      },
      videoTag: {
        key: 'video_labels',
        render: ({ videoTag }: any) => {
          return getOptionsElement('videoTag', videoTag);
        },
      },
      videoType: {
        key: 'video_types',
        render: ({ videoType }: any) => {
          return getOptionsElement('videoType', videoType);
        },
      },
      reviewers: {
        key: 'reviewers%_%exclude_reviewers',
        render: (data: any) => {
          const isInclude = has(data, 'reviewers');
          return getOptionsElement(
            'reviewers',
            isInclude ? data.reviewers : data.exclude_reviewers,
            isInclude
              ? formatMessage({ id: 'Include' })
              : formatMessage({ id: 'Exclude' }),
          );
        },
      },
      steamIds: {
        key: 'steam_ids%_%exclude_steamIds',
        render: (data: any) => {
          const isInclude = has(data, 'steam_ids');
          return getOptionsElement(
            'steamIds',
            isInclude ? data.steam_ids : data.exclude_steam_ids,
            isInclude
              ? formatMessage({ id: 'Include' })
              : formatMessage({ id: 'Exclude' }),
          );
        },
      },
      funny: {
        key: 'funnys',
        render: ({ funnys }: any) => {
          return getOptionsElement('funnys', funnys);
        },
      },
      helpful: {
        key: 'helpfuls',
        render: ({ helpfuls }: any) => {
          return getOptionsElement('helpfuls', helpfuls);
        },
      },
      engagementsRange: {
        key: 'engagements',
        render: ({ engagementsRange }: any) => {
          return getOptionsElement('engagementsRange', engagementsRange);
        },
      },
      earlyAccess: {
        key: 'early_access',
        render: ({ earlyAccess }: any) => {
          return getOptionsElement('earlyAccess', earlyAccess);
        },
      },
      steamPurchase: {
        key: 'steam_purchase',
        render: ({ steamPurchase }: any) => {
          return getOptionsElement('steamPurchase', steamPurchase);
        },
      },
      aigc: {
        key: 'aigc',
        render: ({ aigc }: any) => {
          return getOptionsElement('aigc', aigc);
        },
      },
      commentsRange: {
        key: 'comments',
        render: ({ commentsRange }: any) => {
          return getOptionsElement('commentsRange', commentsRange);
        },
      },
      sharesRange: {
        key: 'shares',
        render: ({ sharesRange }: any) => {
          return getOptionsElement('sharesRange', sharesRange);
        },
      },
      likesRange: {
        key: 'likes',
        render: ({ likesRange }: any) => {
          return getOptionsElement('likesRange', likesRange);
        },
      },
      reviewType: {
        key: 'review_types',
        render: ({ reviewType }: any) => {
          return getOptionsElement('reviewType', reviewType);
        },
      },
      sentimentClause: {
        key: 'sentiment_clause',
        render: ({ sentimentClause }: any) => {
          return getOptionsElement('sentimentClause', sentimentClause);
        },
      },
      influencersType: {
        key: 'officials',
        render: ({ influencersType }: any) => {
          return getOptionsElement('influencersType', influencersType);
        },
      },
      refunded: {
        key: 'refunded',
        render: ({ refunded }: any) => {
          return getOptionsElement('refunded', refunded);
        },
      },
      recordTimes: {
        key: 'record_times',
        render: ({ recordTimes }: any) => {
          return getOptionsElement('recordTimes', recordTimes);
        },
      },
      reviewTimes: {
        key: 'review_times',
        render: ({ reviewTimes }: any) => {
          return getOptionsElement('reviewTimes', reviewTimes);
        },
      },
      freePlayed: {
        key: 'free_played',
        render: ({ freePlayed }: any) => {
          return getOptionsElement('freePlayed', freePlayed);
        },
      },
      starredTag: {
        key: 'starred_tag',
        render: ({ starredTag }: any) => {
          return getOptionsElement('starredTag', starredTag);
        },
      },
      brandAffinitys: {
        key: 'brand_affinitys',
        render: ({ brandAffinitys }: any) => {
          return getOptionsElement('brandAffinitys', brandAffinitys);
        },
      },
      totalPublications: {
        key: 'publications',
        render: ({ totalPublications }: any) => {
          return getOptionsElement('totalPublications', totalPublications);
        },
      },
      avgViews: {
        key: 'avg_views',
        render: ({ avgViews }: any) => {
          return getOptionsElement('avgViews', avgViews);
        },
      },
      views: {
        key: 'views',
        render: ({ views }: any) => {
          return getOptionsElement('views', views);
        },
      },
      avgEngagementRate: {
        key: 'avg_engagements_rate',
        render: ({ avgEngagementRate }: any) => {
          return getOptionsElement('avgEngagementRate', avgEngagementRate);
        },
      },
      brandRelatedPublications: {
        key: 'brand_publications',
        render: ({ brandRelatedPublications }: any) => {
          return getOptionsElement(
            'brandRelatedPublications',
            brandRelatedPublications,
          );
        },
      },
      brandRelatedAvgViews: {
        key: 'avg_brand_views',
        render: ({ brandRelatedAvgViews }: any) => {
          return getOptionsElement(
            'brandRelatedAvgViews',
            brandRelatedAvgViews,
          );
        },
      },
      genreGameList: {
        key: 'game_covereds',
        render: ({ genreGameList }: any) => {
          return getOptionsElement('genreGameList', genreGameList);
        },
      },
      followerRange: {
        key: 'followers',
        render: ({ followerRange }: any) => {
          return getOptionsElement('followerRange', followerRange);
        },
      },
      competitorGames: {
        key: 'games',
        render: ({ competitorGames }: any) => {
          return getOptionsElement('competitorGames', competitorGames);
        },
      },
      urlAnalysis: {
        key: 'content_urls%_%exclude_content_urls',
        render: (data: any) => {
          const { content_urls, exclude_content_urls } = data || {};
          const isInclude = has(data, 'content_urls');
          const urlList =
            (isInclude ? content_urls : exclude_content_urls) || [];

          const isAll = urlList.length === 0;
          return (
            <>
              <span className="model-form-text">
                {formatMessage({ id: isInclude ? 'Include' : 'Exclude' })}
              </span>
              <span className="model-form-value">
                {isAll
                  ? formatMessage({ id: 'All' })
                  : (urlList || []).map((item: string) => {
                      return <p className="model-form-value-text">{item}</p>;
                    })}
              </span>
            </>
          );
        },
      },
      postAuthorType: {
        key: 'organization',
        render: ({ postAuthorType }: any) => {
          return getOptionsElement('postAuthorType', postAuthorType);
        },
      },
      newsDataSource: {
        key: 'news_data_source',
        render: ({ newsDataSource }: any) => {
          return getOptionsElement('newsDataSource', newsDataSource);
        },
      },
      newsCoverageType: {
        key: 'news_coverage_type',
        render: ({ newsCoverageType }: any) => {
          return getOptionsElement('newsCoverageType', newsCoverageType);
        },
      },
      newsBate: {
        key: 'news_beat',
        render: ({ newsBate }: any) => {
          return getOptionsElement('newsBate', newsBate);
        },
      },
      newsUVPM: {
        key: 'uvpm',
        render: ({ newsUVPM }: any) => {
          return getOptionsElement('newsUVPM', newsUVPM);
        },
      },
      // keyKol: {
      //   key: 'key_kols',
      //   render: ({ key_kols }: any) => {
      //     return getOptionsElement("keyKol", key_kols);
      //   },
      // },
      // followerRange: { key: '', render: '' },
      // gptSelect: { key: '', render: '' },
      // gameType: { key: '', render: '' },
      // channelType: { key: '', render: '' },
      // dataSource: { key: '', render: '' },
    };
  }, [formatMessage, gameInfo]);

  const showDataInfo = useMemo(() => {
    return { ...(showData || {}) };
  }, [showData]);

  function singleRenderItem(renderFilter: FilterKeys, label: string) {
    if (![...filter, ...onlySaveFilters].includes(renderFilter)) {
      return null;
    }
    const item = filterOptionsElementMap[renderFilter] as any;
    const { key, render, className } = item;

    return (
      <Form.Item
        label={formatMessage({ id: label || NO_DATE.EMPTY })}
        showStatusIcon={false}
        className="dbo-model-form-item-top"
      >
        <Checkbox
          key={key}
          className={`model-form-checkbox ${className}`}
          value={selectedFilters.includes(key)}
          onChange={(value) => {
            if (value) {
              setSelectedFilters([...selectedFilters, key]);
            } else {
              setSelectedFilters(
                selectedFilters.filter((item) => item !== key),
              );
            }
          }}
        >
          {render(showDataInfo)}
        </Checkbox>
      </Form.Item>
    );
  }

  const keywordsSelectElement = useMemo(() => {
    return singleRenderItem('keyWords', 'Keywords');
  }, [filterOptionsElementMap, selectedFilters, showDataInfo]);

  const urlSelectElement = useMemo(() => {
    return singleRenderItem('urlAnalysis', 'URL');
  }, [filterOptionsElementMap, selectedFilters, showDataInfo]);

  const [filterOptionsElement, setFilterOptionsElement] = useState<any[]>([]);
  const [allSelectedKey, setAllSelectKey] = useState<any[]>([]);

  useEffect(() => {
    const options = filter?.filter(
      (key: string) =>
        !['keyWords', 'urlAnalysis', ...(renderExcludeFilter || [])].includes(
          key,
        ),
    );

    // 添加默认值
    const allSelectedKey: Array<string> = [];

    if (keywordsSelectElement !== null) {
      allSelectedKey.push(filterOptionsElementMap.keyWords?.key as string);
    }
    if (urlSelectElement !== null) {
      allSelectedKey.push(filterOptionsElementMap.urlAnalysis?.key as string);
    }

    const element = options.map((filterKey: FilterKeys) => {
      const item = filterOptionsElementMap[filterKey];
      if (!item) {
        return null;
      }
      const { key, render, className } = item;
      allSelectedKey.push(key);
      return (
        <Checkbox
          key={key}
          className={`model-form-checkbox ${className}`}
          name={key}
        >
          {render(showDataInfo)}
        </Checkbox>
      );
    });

    allSelectedFilters.current = allSelectedKey;
    setSelectedFilters(allSelectedKey);
    setFilterOptionsElement(element);
    setAllSelectKey(allSelectedKey);
  }, [filter, renderExcludeFilter, showDataInfo]);

  const close = () => {
    setFilterName('');
    setTipsMessage('');
    setTimeType('quick');
    setSelectedFilters(allSelectedKey);
    propsClose();
  };

  const getSelectedFilterData = useCallback(() => {
    const keys = selectedFilters.reduce(
      (pre: Array<string>, cur: string) => [
        ...pre,
        ...(cur.split('%_%') || []),
      ],
      [],
    );
    let info: any = {
      start_time: serverData.start_time,
      end_time: serverData.end_time,
      date_type: serverData.date_type,
    };
    if (timeType === 'quick') {
      info = { time_value: value, date_type: serverData.date_type };
    }

    keys.forEach((key) => {
      const dataKey = deviationKeys[key] || key;
      if (serverData?.[dataKey]) {
        info[key] = serverData[dataKey];
      }
    });
    return dealwithChannelToSave(info);
  }, [selectedFilters, serverData, timeType]);

  const saveFilterSetting = () => {
    const filterCond = getSelectedFilterData();

    saveFilter(filterCond, () => {
      const filterInfo = dealwithHistoryToRender(
        filterCond,
        gameInfo?.entity_type === 'mobile',
      );
      saveFilterName(gameInfo, filterName, 'A');
      setSessionStorage(filterInfo, gameInfo, true);
      confirm(filterInfo, filterName);
      close();
    });
  };

  return (
    <Modal
      visible={saveOpen}
      size="l"
      caption={formatMessage({ id: 'Save Filter Setting' })}
      className="dbo-save-model"
      onClose={close}
    >
      <Modal.Body>
        <div className="model-text">
          {formatMessage({
            id: 'Saved setting can be used in all filters in Opinions',
          })}
        </div>
        <Form className="model-form">
          <Form.Item
            status={tipsMessage ? 'error' : undefined}
            label={formatMessage({ id: 'Setting Name' })}
            showStatusIcon={false}
            message={tipsMessage}
          >
            <Input
              placeholder={formatMessage({ id: 'Please enter a name' })}
              className="model-input"
              value={filterName}
              onChange={(value) => {
                setFilterName(value);
                verifyName(value);
              }}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="dbo-tab-flex">
                <BubbleCommon
                  placement="top"
                  tipsMap={commonTimeTipsText(
                    timeShowInfo,
                    quickShowTime || 'Today',
                  )}
                >
                  {formatMessage({ id: 'Time Settings' })}
                </BubbleCommon>
              </div>
            }
            showStatusIcon={false}
          >
            <RadioGroup
              className="model-group"
              value={timeType}
              onChange={(value) => setTimeType(value)}
            >
              <Radio name="default" className="model-group-item">
                <span className="model-form-text">
                  {formatMessage({ id: 'Start&End Date' })}
                </span>
                <span>{timeShowInfo.join(' ~ ')}</span>
              </Radio>
              <Radio name="quick">
                <span className="model-form-text">
                  {formatMessage({ id: 'Date Duration' })}
                </span>
                <span>{quickShowTime}</span>
              </Radio>
            </RadioGroup>
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'Other Settings' })}
            showStatusIcon={false}
          >
            <CheckboxGroup
              className="model-form-group"
              value={selectedFilters}
              onChange={(value) => setSelectedFilters(value)}
            >
              {filterOptionsElement}
            </CheckboxGroup>
          </Form.Item>
          {urlSelectElement}
          {keywordsSelectElement}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={!filterName}
          onClick={saveFilterSetting}
          loading={saveLoading}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
